import React from 'react';
import { useIntl } from 'react-intl';
import RedditPixel from 'react-reddit-pixel';

import { useCurrentTheme } from 'app/shared/theme';
import { Event } from 'app/shared/typings';
import { Responsive } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';
import { ActionButton } from 'app/shared/components/molecules';
import {
  includeBookingFeeInDisplayPrice,
  useBookingFeeMessage,
} from 'app/fan/utils/bookingFee';
import { useEventPageContext } from 'app/fan/context/EventPage';
import {
  calcEarlyBirdOriginalDisplayPrice,
  calcEventDisplayPrice,
  useVisaPromo,
} from 'app/fan/components/pages/Event/eventUtils';

import { EventCTAPriceAndDescription } from './EventCTAPriceAndDescription';
import { VisaDisclaimer } from './VisaDisclaimer';

interface EventCTAWidgetProps {
  onClickGetTickets?: () => void;
  isBuyTicketsCTAEnabled: boolean;
  isDrawerOpen?: boolean;
  event: Event;
  eventAttendeeExists: boolean;
  maxTicketsBought: boolean;
}

export const EventCTAWidget: React.FC<
  React.PropsWithChildren<EventCTAWidgetProps>
> = ({
  onClickGetTickets,
  isBuyTicketsCTAEnabled = true,
  isDrawerOpen = false,
  event,
  eventAttendeeExists,
  maxTicketsBought,
}) => {
  const intl = useIntl();
  const { colors, dimensions } = useCurrentTheme();
  const {
    canRedeemVisaFreeTicket,
    eventIsVisaEligible,
    visaFreeTicketRedeemedForEvent,
    userIsVisaEligible,
    canBypassVisaValidation,
    cardType,
  } = useVisaPromo({ event });
  const { shouldDisplayVisaDisclaimer } = useEventPageContext();
  const feeIncludedInDisplayPrice = includeBookingFeeInDisplayPrice(event);
  const bookingFeeMessage = useBookingFeeMessage({
    event,
    userIsVisaEligible,
    canBypassVisaValidation,
  });

  function handleClickGetTickets() {
    onClickGetTickets && onClickGetTickets();
    RedditPixel.init('a2_ekhq3a2jqrod');
    RedditPixel.track('AddToCart');
  }

  if (!event) {
    return null;
  }

  const onPresale = !!event.onPresale;
  const ticketPrice = calcEventDisplayPrice(event, feeIncludedInDisplayPrice);
  const earlyBirdOriginalTicketPrice = calcEarlyBirdOriginalDisplayPrice(
    event,
    feeIncludedInDisplayPrice
  );
  const ticketCurrency = event.city.country.currencySymbol;

  function ctaIntlId() {
    if (event.cancelled) {
      return 'event.eventCTA.canceled';
    } else if (maxTicketsBought) {
      return 'event.eventCTA.maxTickets';
    } else if (event.isSoldOut && eventAttendeeExists) {
      return 'event.eventCTA.soldOut';
    } else if (!event.isAvailable && eventAttendeeExists) {
      return 'event.eventCTA.ticketSalesPaused';
    } else if (eventAttendeeExists) {
      return 'event.eventCTA.getMoreTickets';
    }

    return 'event.eventCTA.getTickets';
  }

  return (
    <Box
      px={dimensions.horizontalPadding.pageGridItem}
      mx={0}
      backgroundColor={colors.primary.white.base}
    >
      <Responsive hideUp="smallDesktop">
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          py={6}
          px={[0, 4, 0, 0]}
        >
          <EventCTAPriceAndDescription
            bookingFeeMessage={bookingFeeMessage}
            shouldDisplayBookingFeeMessage={!shouldDisplayVisaDisclaimer}
            ticketPrice={ticketPrice}
            ticketCurrency={ticketCurrency}
            isEarlyBird={event.isEarlyBird}
            earlyBirdOriginalTicketPrice={earlyBirdOriginalTicketPrice}
          />
          <Box alignItems="center" justifyContent="flex-end" flex="2">
            <ActionButton
              variant="primary"
              onClick={handleClickGetTickets}
              data-qaid="event-page-cta-widget-button"
              disabled={!isBuyTicketsCTAEnabled || isDrawerOpen}
            >
              {intl.formatMessage({ id: ctaIntlId() })}
            </ActionButton>
          </Box>
        </Box>
      </Responsive>
      {shouldDisplayVisaDisclaimer && (
        <VisaDisclaimer
          onPresale={onPresale}
          eventIsVisaEligible={eventIsVisaEligible}
          canRedeemVisaFreeTicket={canRedeemVisaFreeTicket}
          visaFreeTicketRedeemedForEvent={visaFreeTicketRedeemedForEvent}
          cardType={cardType}
        />
      )}
    </Box>
  );
};
